// ---------- ink color ----------
// Isolated in its own module: rendering needs to tint symbols for the length of an
// animation, and no alphabet should have to worry about it.

/** Une encre proposée au joueur. */
export interface Ink {
  id: string;
  label: string;
  css: string;
}

/**
 * Les quatre encres, et le noir n'est pas la première.
 *
 * Le papier est crème (#fbf6ec) et les cases sont petites : ce qu'il faut d'une encre, c'est
 * qu'elle tienne le contraste à 3 px de côté sans cogner. Les quatre sont entre 8,6 et 11,8
 * pour 1 sur ce papier — largement au-dessus des 7:1 du niveau AAA — donc le choix est un
 * choix de GOÛT et jamais un choix de lisibilité : aucune ne rend une feuille plus dure à lire
 * qu'une autre.
 *
 * L'encre bleue par défaut plutôt que le graphite : sur un aplat crème, un noir pur creuse un
 * trou là où un bleu d'encre pose un trait. C'est la couleur des stylos à plume et des
 * gravures d'atlas, elle reste sombre, et elle n'invente pas une teinte au dessin — elle le
 * refroidit d'un rien. Le graphite d'origine reste à un tap de là, pour qui préfère le noir.
 */
export const INKS: readonly Ink[] = [
  { id: 'encre', label: 'Encre', css: '#27356b' },
  { id: 'graphite', label: 'Graphite', css: '#1c1830' },
  { id: 'sepia', label: 'Sépia', css: '#4a3423' },
  { id: 'sousbois', label: 'Sous-bois', css: '#1f4436' },
];

export const DEFAULT_INK = INKS[0].id;

let base = INKS[0].css;
let override: string | null = null;

/** L'encre choisie dans les réglages. Un id inconnu — une préférence écrite par une version
 *  qui proposait autre chose — retombe sur celle par défaut plutôt que de peindre en rien. */
export function setInk(id: string): void {
  base = (INKS.find((i) => i.id === id) ?? INKS[0]).css;
}

/**
 * Default ink color. Une grille fine appelle ceci des milliers de fois par image : c'est une
 * valeur en mémoire et non plus une lecture de variable CSS, qui forçait un recalcul de style
 * à chaque appel.
 */
export function inkBase(): string {
  return base;
}

export function inkColor(): string {
  return override ?? inkBase();
}

export function setInkOverride(col: string | null): void {
  override = col;
}

/**
 * The three channels of a colour, from `#rrggbb` as well as from the `rgb(r, g, b)` a custom
 * property comes back as.
 *
 * Both forms, because the charter's tokens are written `rgb(var(--x-rgb))` so that one value
 * can serve `var(--acc)` in a rule and `rgba(…, .3)` on the canvas — and reading one back with
 * getPropertyValue substitutes the var, handing us that form and never the hex. Every reader
 * that assumed hex was parsing to NaN the day the charter changed: silently, since NaN
 * channels paint as "no colour at all" rather than as an error.
 */
export function rgbOf(col: string): number[] {
  if (col[0] !== '#') return col.match(/\d+/g)!.slice(0, 3).map(Number);
  const h = col.slice(1);
  // #abc as well as #aabbcc: CSS allows both, and a charter written short would parse to
  // a colour off by a factor of 16 per channel.
  const n = parseInt(
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h,
    16,
  );
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Interpolates two colors (used by the bloom-in flash). */
export function mixColor(a: string, b: string, t: number): string {
  const A = rgbOf(a);
  const B = rgbOf(b);
  const m = (i: number) => Math.round(A[i] + (B[i] - A[i]) * t);
  return `rgb(${m(0)},${m(1)},${m(2)})`;
}
